// client/src/hooks/useChat.js

import { useState, useRef, useEffect, useCallback } from 'react';
import { scrollToBottom, generateId } from '../utils/chatHelpers';
import { savePDFToCache } from '../utils/pdf_cache_setup';
import { fetchWithAuth } from '../context/AuthContext';
import { useCreditUsageContext } from '../context/CreditContext';
import { useSessionState, useSessionRef } from './useSessionState';
import { migrateSessionKey, pruneOldSessions, clearSession, DRAFT_KEY, getSessionRef, getSessionValue, setSessionValue } from '../store/chatSessionStore';
import { resolveCredit402, CREDIT_UNAVAILABLE_MESSAGE } from '../utils/creditHelpers';

const API = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

// If the response is a credit 402 (NO_USABLE_CREDIT /
// USAGE_BLOCKED_AFTER_OVERDRAFT), throw a structured error carrying
// err.status = 402 and err.creditCode so callers can show the product-friendly
// credit-unavailable UI instead of a generic technical error. Non-402 errors
// fall through to the existing generic handling.
async function throwIfCredit402(res) {
  if (res.status !== 402) return;
  let code = null;
  try {
    const body = await res.clone().json();
    code = body && body.code;
  } catch {
    // body not JSON — fall through to generic handling
  }
  const creditCode = resolveCredit402({ status: res.status, code });
  if (creditCode) {
    const err = new Error(CREDIT_UNAVAILABLE_MESSAGE);
    err.status = 402;
    err.creditCode = creditCode;
    throw err;
  }
}

// Module-level ref tracking which project's UI is currently visible.
// SSE callbacks check this to avoid triggering React re-renders for
// inactive chats during the useSyncExternalStore subscription transition.
const activeProjectRef = { current: null };
const isActiveProject = (pid) => pid != null && activeProjectRef.current === pid;

export function useChat(chatId) {

  const credit = useCreditUsageContext();

  // ── Accounting context helper ──────────────────────────────
  // Only sends projectId and turnId — userId is resolved server-side from the JWT.
  const buildAcctCtx = (pid, tid) => ({
    projectId: pid || undefined,
    turnId: tid || undefined,
  });

  const [isLoadingSuggestions, setIsLoadingSuggestions] = useSessionState(chatId, 'isLoadingSuggestions', false);
  const [isLoadingReport, setIsLoadingReport] = useSessionState(chatId, 'isLoadingReport', false);
  const [chatStarted, setChatStarted] = useSessionState(chatId, 'chatStarted', false);
  const [isSending, setIsSending] = useSessionState(chatId, 'isSending', false);
  const [turns, setTurns] = useSessionState(chatId, 'turns', []);
  const turnsRef = useSessionRef(chatId, 'turnsRef', []);
  const [feedbackState, setFeedbackState] = useSessionState(chatId, 'feedbackState', {});
  const [projectId, setProjectId] = useSessionState(chatId, 'projectId', null);
  const [projectName, setProjectName] = useSessionState(chatId, 'projectName', '');
  const [isChatHydrating, setIsChatHydrating] = useSessionState(chatId, 'isChatHydrating', false);
  const [hasMoreTurns, setHasMoreTurns] = useSessionState(chatId, 'hasMoreTurns', false);
  const [turnCursor, setTurnCursor] = useSessionState(chatId, 'turnCursor', null);
  const [isLoadingOlderTurns, setIsLoadingOlderTurns] = useSessionState(chatId, 'isLoadingOlderTurns', false);
  const [activeSuggestionTurnId, setActiveSuggestionTurnId] = useSessionState(chatId, 'activeSuggestionTurnId', null);
  const [suggestions, setSuggestions] = useSessionState(chatId, 'suggestions', {});
  const [typingTurnId, setTypingTurnId] = useSessionState(chatId, 'typingTurnId', null);
  const [typingSuggestionId, setTypingSuggestionId] = useSessionState(chatId, 'typingSuggestionId', null);
  const [extractedSuggestions, setExtractedSuggestions] = useSessionState(chatId, 'extractedSuggestions', {});
  const [selectedSuggestions, setSelectedSuggestions] = useSessionState(chatId, 'selectedSuggestions', {});
  const [suggestionExtractionStatus, setSuggestionExtractionStatus] = useSessionState(chatId, 'suggestionExtractionStatus', {});
  const [reports, setReports] = useSessionState(chatId, 'reports', {});
  const [activeReportId, setActiveReportId] = useSessionState(chatId, 'activeReportId', null);
  const [typingReportId, setTypingReportId] = useSessionState(chatId, 'typingReportId', null);
  const [reportLoadingPhase, setReportLoadingPhase] = useSessionState(chatId, 'reportLoadingPhase', 'init');
  const [validUserMessages, setValidUserMessages] = useSessionState(chatId, 'validUserMessages', []);
  const [allFiles, setAllFiles] = useSessionState(chatId, 'allFiles', []);

  const assistantTurnMappingsRef = useSessionRef(chatId, 'assistantTurnMappingsRef', {});
  const persistedTurnCountRef = useSessionRef(chatId, 'persistedTurnCountRef', 0);
  const reportSuggestionOptionIdsRef = useSessionRef(chatId, 'reportSuggestionOptionIdsRef', {});
  const isLoadedOnceRef = useSessionRef(chatId, 'isLoadedOnceRef', false); // NEW: dedupe guard

  // ─── Refs ─────────────────────────────────────────────────────
  const prevActiveTurnRef = useRef(null);
  const prevActiveReportRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const rightPaneRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const isRightNearBottomRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const userScrolledAwayRightRef = useRef(false);
  // Tracks the Model API request ID of the currently active stream
  // (strictly populated on stream response headers, cleared on stream finish).
  const activeRequestIdRef = useRef(null);
  // Tracks whether the user has explicitly requested Stop for the current generation.
  const stopRequestedRef = useRef(false);
  // Set of request IDs for which cancellation has already been dispatched.
  const cancelledRequestIdsRef = useRef(new Set());
  // Synchronous lock to prevent rapid double-clicks on Stop before React re-renders.
  const isStoppingRef = useRef(false);
  // UI state reflecting that Stop has been clicked and cancellation is in-flight.
  const [isStopping, setIsStopping] = useState(false);

  // Keep module-level ref in sync so visibility guards (scroll, right-pane)
  // use the URL as the authoritative visible-session identity, not mutable
  // session-store projectId which can be stale during transitions.
  useEffect(() => {
    activeProjectRef.current = chatId || DRAFT_KEY;
    return () => {
      if (activeProjectRef.current === (chatId || DRAFT_KEY)) activeProjectRef.current = null;
    };
  }, [chatId]);

  // ─── Unmount-only abort for in-flight streams ──────────────────
  // Navigation between chats must NOT cancel stream operations —
  // they continue writing to their immutable operation-project bucket.
  // Abort only on true unmount (route away from Home entirely).
  const streamAbortControllersRef = useRef(new Set());
  useEffect(() => {
    return () => {
      for (const ctrl of streamAbortControllersRef.current) {
        ctrl.abort();
      }
      streamAbortControllersRef.current.clear();
    };
  }, []);

  // ─── Sync setter: updates both turns state (for UI) and turnsRef (for agent calls) ──
  // ─── Canonical history writer: ref first, UI state second ───────────────────────────
  const commitTurns = (nextTurns) => {
    turnsRef.current = nextTurns;
    setTurns(nextTurns);
  };

  const updateTurns = (updater) => {
    const nextTurns =
      typeof updater === 'function'
        ? updater(turnsRef.current)
        : updater;

    turnsRef.current = nextTurns;
    setTurns(nextTurns);
    return nextTurns;
  };

  const generateToolCallId = () => `toolu_${generateId().replace(/[^a-zA-Z0-9]/g, '').slice(0, 24)}`;

  const getExtractOptimizationFromKwargs = useCallback((kwargs) => {
    if (!kwargs || typeof kwargs !== 'object' || Array.isArray(kwargs)) return {};
    return normalizeExtractedSuggestions(kwargs.extract_optimization || kwargs);
  }, []);

  const getReportCountFromKwargs = useCallback((kwargs) => {
    if (!kwargs || typeof kwargs !== 'object' || Array.isArray(kwargs)) return 0;
    const count = Number(kwargs.report_count);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }, []);

  const buildAcceptedOptimizations = useCallback((analysisTurnId, checkedSuggestions) => {
    const extracted = extractedSuggestions[analysisTurnId] || {};
    return checkedSuggestions.reduce((acc, key) => {
      if (typeof extracted[key] === 'string' && extracted[key].trim()) {
        acc[key] = extracted[key];
      }
      return acc;
    }, {});
  }, [extractedSuggestions]);

  const setReportEntry = useCallback((analysisTurnId, reportEntry) => {
    setReports(prev => {
      const currentEntries = prev[analysisTurnId] || [];
      const existingIndex = currentEntries.findIndex(entry => entry.turnId === reportEntry.turnId);
      const nextEntries = [...currentEntries];

      if (existingIndex === -1) {
        nextEntries.push(reportEntry);
      } else {
        nextEntries[existingIndex] = {
          ...nextEntries[existingIndex],
          ...reportEntry,
        };
      }

      nextEntries.sort((a, b) => (a.version || 0) - (b.version || 0));
      return {
        ...prev,
        [analysisTurnId]: nextEntries,
      };
    });
  }, []);

  const removeReportEntry = useCallback((analysisTurnId, reportTurnId) => {
    setReports(prev => {
      const currentEntries = prev[analysisTurnId] || [];
      const filteredEntries = currentEntries.filter(entry => entry.turnId !== reportTurnId);
      return {
        ...prev,
        [analysisTurnId]: filteredEntries,
      };
    });
  }, []);

  // ─── Auto-scroll effects ──────────────────────────────────────
  useEffect(() => {
    if (typingTurnId !== null && !userScrolledAwayRef.current) scrollMessagesToBottom();
  }, [turns, typingTurnId]);

  useEffect(() => {
    if (typingSuggestionId !== null && !userScrolledAwayRightRef.current) scrollRightToBottom();
  }, [suggestions, typingSuggestionId]);

  useEffect(() => {
    if (typingReportId !== null && !userScrolledAwayRightRef.current) scrollRightToBottom();
  }, [reports, typingReportId]);

  // ─── Clear selectedSuggestions when pane changes ─────────────
  useEffect(() => {
    const currentTurnId = activeSuggestionTurnId;
    const currentReportId = activeReportId;
    // Determine if user navigated away from suggestions view
    const wasViewingSuggestions = prevActiveTurnRef.current !== null && prevActiveReportRef.current === null;
    const isNowViewingReport = currentTurnId !== null && currentReportId !== null;
    const isNowViewingDifferentTurn = prevActiveTurnRef.current !== null && prevActiveTurnRef.current !== currentTurnId;
    const isNowClosingPane = prevActiveTurnRef.current !== null && currentTurnId === null;
    // Clear selections when:
    // 1. Switching from suggestions to report (same turn)
    // 2. Switching to different turn's suggestions
    // 3. Closing the right pane
    if (wasViewingSuggestions && isNowViewingReport && prevActiveTurnRef.current === currentTurnId) {
      const turnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[turnId]; return u; }); //updated
    } else if (isNowViewingDifferentTurn) {
      const prevTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[prevTurnId]; return u; });
    } else if (isNowClosingPane) {
      const prevTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[prevTurnId]; return u; });
    }

    prevActiveTurnRef.current = currentTurnId;
    prevActiveReportRef.current = currentReportId;
  }, [activeSuggestionTurnId, activeReportId]);

  // ─── Scroll helpers ───────────────────────────────────────────
  const scrollMessagesToBottom = () => scrollToBottom(messagesContainerRef);
  const scrollRightToBottom = () => scrollToBottom(rightPaneRef);

  const onScrollMessages = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distance < 80;
    if (typingTurnId !== null && distance > 80) userScrolledAwayRef.current = true;
  };

  const handleRightPaneScroll = () => {
    const el = rightPaneRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isRightNearBottomRef.current = distance < 80;
    if (typingSuggestionId !== null && distance > 80) userScrolledAwayRightRef.current = true;
  };

  // ─── Reset (New Project) ──────────────────────────────────────

  const resetChat = useCallback(() => {
    clearSession(DRAFT_KEY); // wipes only the "new, unsaved chat" bucket
  }, []);

  // ─── Load existing project (sidebar click) ────────────────────
  // const loadProject = useCallback(async (id, name) => {
  const loadProject = async (id, name, signal) => {

    setIsChatHydrating(true);
    setProjectId(id);
    setProjectName(name || '');
    setChatStarted(true);

    try {
      const res = await fetchWithAuth(`${API}/api/projects/${id}/turns?limit=50`, {
        signal,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // If the user navigated away while this fetch was in flight, discard.
      // The currently visible chat should not be overwritten by stale history.
      if (activeProjectRef.current !== id) return;

      // Use project name from API response if not already set
      if (!name && data.projectName) {
        setProjectName(data.projectName);
      }

      // Restore turns into local state format
      const restoredTurns = data.turns.map((t) => ({
        id: t.id,           // DB turn id used as client id for loaded turns
        role: t.role,
        content: t.content || '',
        tool_calls: t.toolCalls || [],
        tool_call_id: t.toolCallId || null,
        kwargs: t.kwargs || {},
        metadata: t.metadata || {},
        status: t.status || 'completed',
        turnNumber: t.turnNumber,
        isLoaded: true,     // flag: this turn came from DB, not typed live
        files: (t.files || []).map(f => ({
          id: f.id,
          name: f.filename,
          type: f.mimeType,
          size: f.size,
          presigned_url: f.presigned_url,   // signed URL from server
          storagePath: f.s3Key,
          uploading: false,
          uploadError: false,
        })),
      }));
      const highestTurnNumber = restoredTurns.reduce(
        (max, turn) => Math.max(max, Number(turn.turnNumber) || 0),
        0
      )

      persistedTurnCountRef.current = highestTurnNumber;
      commitTurns(restoredTurns);
      setHasMoreTurns(data.hasMore);
      setTurnCursor(data.nextCursor);

      const restoredFeedback = {};
      const restoredSuggestionsText = {};
      const restoredExtractedSuggestions = {};
      const restoredExtractionStatus = {};
      const reportsToRestore = {};
      const analysisTurnIds = new Set();

      data.turns.forEach((t) => {
        if (t.role === 'analysis') {
          analysisTurnIds.add(t.id);
        }

        if (t.role === 'assistant') {
          assistantTurnMappingsRef.current[t.id] = t.id;
        }

        // ── Unified feedback restoration (all roles: assistant/analysis/report) ──
        if (t.feedback?.length > 0) {
          const fb = t.feedback[0];
          restoredFeedback[t.id] = {
            type: fb.type,
            comment: fb.comment || '',
          };
        }

        if (t.role === 'analysis') {
          const extracted = getExtractOptimizationFromKwargs(t.kwargs);

          restoredExtractedSuggestions[t.id] = extracted;
          restoredSuggestionsText[t.id] = t.content || '';
          restoredExtractionStatus[t.id] =
            Object.keys(extracted).length > 0 ? 'done' : 'idle';

          reportSuggestionOptionIdsRef.current[t.id] = Object.fromEntries(
            (t.suggestionOptions || []).map((opt) => [opt.title, opt.id])
          );
        }

        if (t.role === 'report') {
          const analysisTurnId = t.kwargs?.analysis_turn_id;
          const version = Number(t.kwargs?.version) || 1;
          const acceptedOptimizations =
            t.kwargs?.accepted_optimizations || []

          if (analysisTurnId && analysisTurnIds.has(analysisTurnId)) {
            reportsToRestore[analysisTurnId] = [
              ...(reportsToRestore[analysisTurnId] || []),
              {
                turnId: t.id,
                version,
                content: t.content || '',
                isComplete: t.status === 'completed' || !t.status,
                status: t.status || 'completed',
                selectedSuggestions: acceptedOptimizations,
                acceptedOptimizations,
                analysisTurnId,
              },
            ];
          }
        }
      });

      Object.keys(reportsToRestore).forEach((analysisTurnId) => {
        reportsToRestore[analysisTurnId].sort((a, b) => a.version - b.version);
      });

      // ✅ All state updates batched — single re-render
      setSuggestions(prev => ({ ...prev, ...restoredSuggestionsText }));
      setExtractedSuggestions(prev => ({ ...prev, ...restoredExtractedSuggestions }));
      setSuggestionExtractionStatus(prev => ({ ...prev, ...restoredExtractionStatus }));
      setReports(prev => ({ ...prev, ...reportsToRestore }));
      setFeedbackState(restoredFeedback);

      // Restore validUserMessages for API calls
      const validMsgs = data.turns
        .filter((t) => t.role === 'user' && t.content?.trim())
        .map((t) => t.content);
      setValidUserMessages(validMsgs);

      const restoredAllFiles = data.turns
        .flatMap(t => t.files ?? [])
        .filter(f => f.presigned_url && f.mimeType)
        .map(f => ({ name: f.filename, type: f.mimeType, presigned_url: f.presigned_url }));
      setAllFiles(restoredAllFiles);

      // Mark as loaded only after ALL state is written — so the dedupe guard
      // in the useEffect won't skip this chat on StrictMode remounts
      isLoadedOnceRef.current = id;
    } catch (err) {
      if (err.name === 'AbortError') {
        // Aborted (StrictMode / rapid nav) — treat as a failure for this target id.
        // Do not return early here because we want the finally block below to
        // clear the per-target hydration flag using the immutable `id`.
        console.warn('Project load aborted for', id);
      } else {
        console.error('Error loading project:', err);
      }
    } finally {
      // Clear hydration for the specific target id we attempted to load.
      // Use session-store write scoped to `id` so a late/stale load for B
      // cannot clear C's hydration state.
      try {
        setSessionValue(id, 'isChatHydrating', false);
      } catch (e) {
        // Swallow: defensive in case session store is unavailable.
        console.error('Failed to clear isChatHydrating for', id, e);
      }
    }
  };

  useEffect(() => {
    if (chatId) {
      if (isLoadedOnceRef.current === chatId) return; // already loaded this chat — skip
      // Belt-and-suspenders: don't load from API while handleSend is still streaming for the same chat
      if (isSending && projectId === chatId) return;

      const controller = new AbortController();
      loadProject(chatId, '', controller.signal);

      return () => {
        controller.abort(); // cancel in-flight fetch on chatId change / unmount
      };
    }
    // No else-branch reset needed: landing on "/" just renders the draft bucket naturally.
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Load older turns (scroll to top pagination) ──────────────
  const loadOlderTurns = useCallback(async () => {
    if (!projectId || !hasMoreTurns || isLoadingOlderTurns) return;

    setIsLoadingOlderTurns(true);
    try {
      const res = await fetchWithAuth(
        `${API}/api/projects/${projectId}/turns?cursor=${turnCursor}`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      const olderTurns = data.turns.map((t) => ({
        id: t.id,
        dbTurnId: t.id,
        role: t.role,
        content: t.content || '',
        kwargs: t.kwargs || {},
        metadata: t.metadata || {},
        status: t.status || 'completed',
        files: (t.files || []).map(f => ({
          id: f.id,
          name: f.filename,
          type: f.mimeType,
          size: f.size,
          presigned_url: f.presigned_url,   // signed URL from server
          storagePath: f.s3Key,
          uploading: false,
          uploadError: false,
        })),
        turnNumber: t.turnNumber,
        isLoaded: true,
      }));

      // Preserve scroll position when prepending
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;

      updateTurns(prev => [...olderTurns, ...prev]); // prepend older turns
      const olderExtracted = {};

      data.turns.forEach((t) => {
        if (t.role === 'analysis') {
          olderExtracted[t.id] = getExtractOptimizationFromKwargs(t.kwargs);
          reportSuggestionOptionIdsRef.current[t.id] = Object.fromEntries(
            (t.suggestionOptions || []).map((opt) => [opt.title, opt.id])
          );
        }

        if (t.role === 'report') {
          const analysisTurnId = t.kwargs?.analysis_turn_id;
          if (!analysisTurnId) return;

          const acceptedOptimizations =
            t.kwargs?.accepted_optimizations || []

          setReportEntry(analysisTurnId, {
            turnId: t.id,
            version: Number(t.kwargs?.version) || 1,
            content: t.content || '',
            isComplete: t.status === 'completed' || !t.status,
            status: t.status || 'completed',
            selectedSuggestions: acceptedOptimizations,
            acceptedOptimizations,
            analysisTurnId,
          });
        }
      });
      if (Object.keys(olderExtracted).length > 0) {
        setExtractedSuggestions(prev => ({ ...prev, ...olderExtracted }));
      }
      const olderFiles = data.turns
        .flatMap(t => t.files ?? [])
        .filter(f => f.presigned_url && f.mimeType)
        .map(f => ({ name: f.filename, type: f.mimeType, presigned_url: f.presigned_url }));
      setAllFiles(prev => [...olderFiles, ...prev]);
      setHasMoreTurns(data.hasMore);
      setTurnCursor(data.nextCursor);

      // Restore scroll so user doesn't jump
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });

    } catch (err) {
      console.error('Error loading older turns:', err);
    } finally {
      setIsLoadingOlderTurns(false);
    }
  }, [projectId, hasMoreTurns, turnCursor, isLoadingOlderTurns]);

  // ─── Rename project ───────────────────────────────────────────
  const renameProject = useCallback(async (id, newName) => {
    try {
      const res = await fetchWithAuth(`${API}/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) throw new Error('Rename failed');
      setProjectName(newName);
    } catch (err) {
      console.error('Error renaming project:', err);
    }
  }, []);

  // ─── File upload handler ──────────────────────────────────────
  const handleFileUpload = async (file, turnId, currentProjectId) => {
    if (!currentProjectId || !turnId) {
      console.warn('No projectId or turnId — skipping file upload');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', currentProjectId);
      formData.append('turnId', turnId);
      formData.append('originalFilename', file.name);

      // ⚠️ Do NOT set Content-Type header — browser sets it automatically with boundary
      const res = await fetchWithAuth(`${API}/files/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Upload failed:', err);
        return null;
      }

      const data = await res.json();

      if (data.status === 'success' && data.file) {
        console.log('✅ File uploaded:', data.file.filename);
        return data.file; // { id, filename, storedFilename, storagePath, presigned_url, size, mimeType, uploadedAt }
      }

      return null;
    } catch (error) {
      console.error('File upload error:', error);
      return null;
    }
  };

  // ─── API helpers ──────────────────────────────────────────────

  const buildFileMessages = (uploadedFiles) => {
    return uploadedFiles
      .filter(f => f.presigned_url)
      .map(f => ({ name: f.name, type: f.type, presigned_url: f.presigned_url }));
  };

  // Rejected turns must never reach the model/backend. Applied inside every API
  // helper that sends chat history so a rejected turn can never leak into a
  // payload. ONLY `status: 'rejected'` is excluded — pending/streaming/
  // completed (and any other) turns pass through unchanged.
  const getApiTurns = (turns) =>
    turns.filter(turn => turn && turn.status !== 'rejected');

  const fetchTopicName = async (message, currentProjectId, userTurnId = null) => {
    try {
      const res = await fetchWithAuth(`${API}/api/analysis/get_topicname`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, ...buildAcctCtx(currentProjectId, userTurnId) }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.event === 'success' && data.data.content) {
        await renameProject(currentProjectId, data.data.content);
      }
    } catch (err) {
      console.error('Topic name fetch error:', err);
      // non-fatal — project keeps temp name
    }
  };

  const patchTurn = async (projectId, turnId, payload) => {
    const res = await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${turnId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to patch turn');
    return data;
  };

  const normalizeExtractedSuggestions = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        ([key, val]) => typeof key === 'string' && key.trim() && typeof val === 'string'
      )
    );
  };

  const persistExtractedSuggestions = async (projectId, turnId, extracted) => {
    const normalized = normalizeExtractedSuggestions(extracted);
    await patchTurn(projectId, turnId, {
      kwargs: {
        extract_optimization: normalized,
      },
    });
    return normalized;
  };

  const ensureExtractedSuggestions = async (projectId, turnId, analysisText) => {
    const existing = extractedSuggestions[turnId];
    if (existing && Object.keys(existing).length > 0) {
      return existing;
    }

    setSuggestionExtractionStatus(prev => ({
      ...prev,
      [turnId]: 'loading',
    }));

    const extractedResponse = await extractOptimizations(analysisText);
    const extracted =
      extractedResponse?.data.content ||
      extractedResponse ||
      {};

    const normalized = normalizeExtractedSuggestions(extracted);

    setExtractedSuggestions(prev => ({
      ...prev,
      [turnId]: normalized,
    }));
    setSuggestionExtractionStatus(prev => ({
      ...prev,
      [turnId]: Object.keys(normalized).length > 0 ? 'done' : 'idle',
    }));

    if (projectId && turnId) {
      await persistExtractedSuggestions(projectId, turnId, normalized);
    }

    return normalized;
  };

  const onEnsureExtractedSuggestions = async (turnId) => {
    const analysisTurn = turnsRef.current.find(turn => turn.id === turnId);
    if (!analysisTurn) return {};
    return ensureExtractedSuggestions(projectId, turnId, analysisTurn.content || suggestions[turnId] || '');
  };

  const createTurnPayload = (role, content = '', extra = {}) => ({
    turnNumber: persistedTurnCountRef.current + 1,
    role,
    content,
    ...extra,
  });

  const createLocalTurnFromPayload = useCallback((id, payload) => ({
    id,
    role: payload.role,
    content: payload.content || '',
    tool_calls: payload.toolCalls || [],
    tool_call_id: payload.toolCallId || null,
    kwargs: payload.kwargs || {},
    metadata: payload.metadata || {},
    turnNumber: payload.turnNumber,
    status: payload.status || 'pending',
    files: payload.files || [],
  }), []);

  const persistTurn = async (currentProjectId, payload) => {
    const res = await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Failed to create ${payload.role} turn`);
    }

    persistedTurnCountRef.current = payload.turnNumber;
    return data.turnId || data.id;
  };

  const createAndAppendTurn = async (currentProjectId, payload, localOverrides = {}) => {
    const dbTurnId = currentProjectId ? await persistTurn(currentProjectId, payload) : generateId();
    const localTurn = {
      ...createLocalTurnFromPayload(dbTurnId, payload),
      ...localOverrides,
      id: dbTurnId,
    };

    updateTurns(prev => [...prev, localTurn]);
    return { dbTurnId, localTurn };
  };

  const upsertAssistantTurn = (assistantId, updater) => {
    const prevTurns = turnsRef.current;
    const index = prevTurns.findIndex(t => t.id === assistantId);

    let nextTurns;

    if (index === -1) {
      const created =
        typeof updater === 'function'
          ? updater(null)
          : updater;

      nextTurns = created ? [...prevTurns, created] : prevTurns;
    } else {
      const current = prevTurns[index];
      const updated =
        typeof updater === 'function'
          ? updater(current)
          : updater;

      if (!updated) {
        nextTurns = prevTurns;
      } else {
        nextTurns = [...prevTurns];
        nextTurns[index] = updated;
      }
    }

    turnsRef.current = nextTurns;
    setTurns(nextTurns);
    return nextTurns;
  };

  const sendCancelRequest = useCallback(async (requestId) => {
    if (!requestId || cancelledRequestIdsRef.current.has(requestId)) {
      return;
    }
    cancelledRequestIdsRef.current.add(requestId);

    try {
      const res = await fetchWithAuth(`${API}/api/analysis/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId }),
      });
      if (!res.ok && res.status !== 404) {
        console.warn('Cancellation request returned status:', res.status);
      }
    } catch (err) {
      console.error('Failed to trigger generation cancellation:', err);
    }
  }, []);

  const callAgent = async (turns, onDelta, signal, acctCtx = {}) => {
    let invocationId = null;
    let finalData = null;
    try {
      const res = await fetchWithAuth(`${API}/api/analysis/agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turns: getApiTurns(turns), ...acctCtx }),
        signal,
      });
      if (!res.ok) {
        await throwIfCredit402(res);
        const text = await res.text().catch(() => '');
        throw new Error(`Agent error ${res.status}: ${text}`);
      }

      // Accounting invocation ID — used to attach the real assistant Turn
      // ID to the invocation once the Turn is persisted.
      invocationId = res.headers.get('x-invocation-id') || null;
      const requestId = res.headers.get('x-request-id') || null;
      if (requestId) {
        activeRequestIdRef.current = requestId;
        if (stopRequestedRef.current) {
          sendCancelRequest(requestId);
        }
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            let evt;
            try {
              evt = JSON.parse(trimmed);
            } catch {
              continue;
            }

            if (evt.event === 'delta' && typeof evt.data === 'string') {
              onDelta?.(evt.data || '');
            } else if (evt.event === 'final') {
              finalData = { ...evt.data, cancelled: Boolean(evt.cancelled || evt.data?.response_metadata?.stop_reason === 'cancelled') };
            } else if (evt.event === 'error') {
              if (stopRequestedRef.current) {
                finalData = { ...(finalData || {}), cancelled: true };
                break;
              }
              throw new Error(evt.data || evt.message || 'Agent stream error');
            }
          }
        }

        if (buffer.trim()) {
          try {
            const evt = JSON.parse(buffer.trim());
            if (evt.event === 'delta' && typeof evt.data === 'string') {
              onDelta?.(evt.data);
            } else if (evt.event === 'final') {
              finalData = { ...evt.data, cancelled: Boolean(evt.cancelled || evt.data?.response_metadata?.stop_reason === 'cancelled') };
            }
          } catch { /* ignore malformed trailing data caused by abrupt cancellation/EOF */ }
        }
      } finally {
        activeRequestIdRef.current = null;
      }

      if (stopRequestedRef.current) {
        finalData = { ...(finalData || {}), cancelled: true };
      }

      return { finalData, invocationId };
    } catch (err) {
      activeRequestIdRef.current = null;
      if (stopRequestedRef.current) {
        return {
          finalData: finalData ? { ...finalData, cancelled: true } : { cancelled: true },
          invocationId,
        };
      }
      if (err.name === 'AbortError') throw new Error('STREAM_ABORTED'); // intentional abort — not a failure
      throw err;
    }
  }

  // Attach a started AIInvocation to the real, persisted assistant Turn.
  // Only ever called with a real DB Turn ID (never a client-generated one).
  const attachInvocationToTurn = async (invocationId, turnId) => {
    const res = await fetchWithAuth(`${API}/api/analysis/invocations/${invocationId}/attach-turn`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turnId }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Attach error ${res.status}: ${text}`);
    }
    return res.json();
  };

  const streamAnalysisTool = async (args, currentTurns, onDelta, signal, acctCtx = {}) => {
    try {
    const res = await fetchWithAuth(`${API}/api/analysis/execute-tool`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'get_analysis', args, turns: getApiTurns(currentTurns).slice(0, -1), ...acctCtx }),
      signal,
    });

    if (!res.ok) {
      await throwIfCredit402(res);
      const text = await res.text().catch(() => '');
      throw new Error(`get_analysis error ${res.status}: ${text}`);
    }

    const requestId = res.headers.get('x-request-id') || null;
    if (requestId) {
      activeRequestIdRef.current = requestId;
      if (stopRequestedRef.current) {
        sendCancelRequest(requestId);
      }
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          const evt = JSON.parse(trimmed);

          if (evt.event === 'delta' && typeof evt.data === 'string') {
            onDelta?.(evt.data);
          } else if (evt.event === 'final') {
            finalData = { ...evt.data, cancelled: Boolean(evt.cancelled || evt.data?.response_metadata?.stop_reason === 'cancelled') };
          } else if (evt.event === 'error') {
            throw new Error(evt.message || evt.data || 'Analysis stream error');
          }
        }
      }

      if (buffer.trim()) {
        const evt = JSON.parse(buffer.trim());
        if (evt.event === 'delta' && typeof evt.data === 'string') onDelta?.(evt.data);
        else if (evt.event === 'final') finalData = { ...evt.data, cancelled: Boolean(evt.cancelled || evt.data?.response_metadata?.stop_reason === 'cancelled') };
        else if (evt.event === 'error') throw new Error(evt.message || evt.data || 'Analysis stream error');
      }
    } finally {
      activeRequestIdRef.current = null;
    }

    return finalData;
    } catch (err) {
      activeRequestIdRef.current = null;
      if (err.name === 'AbortError') throw new Error('STREAM_ABORTED');
      throw err;
    }
  };

  const extractOptimizations = async (assessmentText, acctCtx = {}) => {
    const res = await fetchWithAuth(`${API}/api/analysis/extract_optimizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assessment_text: assessmentText, ...acctCtx }),
    });
    if (!res.ok) {
      await throwIfCredit402(res);
      throw new Error(`Extract error: ${res.status}`);
    }
    return res.json();
  };

  const getMaterialCostList = async (allTurns, accepted_suggestions, acctCtx = {}) => {
    const res = await fetchWithAuth(`${API}/api/analysis/get_material_cost_list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turns: getApiTurns(allTurns), accepted_suggestions, ...acctCtx }),
    });
    if (!res.ok) {
      await throwIfCredit402(res);
      throw new Error(`Cost list error: ${res.status}`);
    }
    return res.json();
  };

  const streamReport = async (allTurns, accepted_suggestions, onChunk, signal, acctCtx = {}) => {
    try {
    const res = await fetchWithAuth(`${API}/api/analysis/generate_report_stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turns: getApiTurns(allTurns), accepted_suggestions, ...acctCtx }),
      signal,
    });
    if (!res.ok) {
      await throwIfCredit402(res);
      throw new Error(`Report stream error: ${res.status}`);
    }

    const requestId = res.headers.get('x-request-id') || null;
    if (requestId) {
      activeRequestIdRef.current = requestId;
      if (stopRequestedRef.current) {
        sendCancelRequest(requestId);
      }
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          let evt;
          try { evt = JSON.parse(trimmed); } catch { continue; }

          if (evt.event === 'delta' && typeof evt.data === 'string') {
            if (!stopRequestedRef.current) {
              onChunk(evt.data);
            }
          } else if (evt.event === 'final') {
            finalData = { ...evt.data, cancelled: Boolean(evt.cancelled || evt.data?.response_metadata?.stop_reason === 'cancelled') };
          } else if (evt.event === 'error') {
            throw new Error(evt.data || 'Report stream error');
          }
        }
      }

      // Flush any remaining buffered line
      if (buffer.trim()) {
        try {
          const evt = JSON.parse(buffer.trim());
          if (evt.event === 'delta' && typeof evt.data === 'string') {
            if (!stopRequestedRef.current) onChunk(evt.data);
          } else if (evt.event === 'final') finalData = { ...evt.data, cancelled: Boolean(evt.cancelled || evt.data?.response_metadata?.stop_reason === 'cancelled') };
        } catch { /* ignore malformed trailing data */ }
      }
    } finally {
      activeRequestIdRef.current = null;
    }

    return finalData;
    } catch (err) {
      activeRequestIdRef.current = null;
      if (err.name === 'AbortError') throw new Error('STREAM_ABORTED');
      throw err;
    }
  };

  const streamText = async (text, onChunk, speed = 15, capturedPid = null) => {
    for (const ch of text) {
      onChunk(ch);
      setTimeout(() => {
        if (!userScrolledAwayRef.current && (!capturedPid || isActiveProject(capturedPid))) scrollMessagesToBottom();
      }, 0);
      await new Promise(r => setTimeout(r, speed));
    }
  };

  // ─── Main send handler ────────────────────────────────────────
  const handleSend = async (text, files) => {
    // ── B1: Prevent concurrent sends using session-owned state (not stale closure) ──
    const guardKey = projectId || chatId || DRAFT_KEY;
    if (getSessionValue(guardKey, 'isSending', false)) return;

    setChatStarted(true);
    setIsSending(true);
    stopRequestedRef.current = false;
    isStoppingRef.current = false;
    cancelledRequestIdsRef.current.clear();
    setIsStopping(false);

    const isFirstMessage = !projectName && !projectId;
    let clientUserTurnId = null;
    let clientAssistantTurnId = null;

    // ── Step A: Resolve operationProjectId (may create project) ──
    let currentProjectId = projectId;
    if (!currentProjectId) {
      const autoName = text?.trim()?.slice(0, 50) || 'Untitled Project';
      try {
        const res = await fetchWithAuth(`${API}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: autoName }),
        });
        const newProject = await res.json();
        currentProjectId = newProject.id;
        migrateSessionKey(chatId || DRAFT_KEY, currentProjectId);
        setProjectId(currentProjectId);
        setProjectName(newProject.name);
        isLoadedOnceRef.current = currentProjectId;
      } catch (err) {
        // ── H2: Project creation failure — stop the send, preserve draft ──
        console.error('Failed to create project:', err);
        setIsSending(false);
        return; // Cannot proceed without a project — conversation would not be persisted
      }
    }

    // ── Compare-and-clear draft (only after send ownership is established) ──
    //    The send has immutable snapshots of text/files captured at click time.
    //    We clear draftText/draftFiles only if the current draft still
    //    represents the submitted content — newer user edits are preserved.
    if (currentProjectId) {
      const clearingKey = currentProjectId;
      const currentDraftText = getSessionValue(clearingKey, 'draftText', '');
      const currentDraftFiles = getSessionValue(clearingKey, 'draftFiles', []);

      // Clear text only if unchanged since the send click
      if (currentDraftText === text) {
        setSessionValue(clearingKey, 'draftText', '');
      }

      // Clear files only if unchanged — compare by stable File identity
      const getFileId = (f) => `${f.name}::${f.size}::${f.lastModified}`;
      const submittedIds = new Set((files || []).map(getFileId));
      const currentIds = new Set((currentDraftFiles || []).map(getFileId));
      if (
        submittedIds.size === currentIds.size &&
        [...submittedIds].every(id => currentIds.has(id))
      ) {
        setSessionValue(clearingKey, 'draftFiles', []);
      }
    }

    // ── Operation-scoped refs & writers (before try — used in catch/finally) ──
    const opKey = currentProjectId || DRAFT_KEY;
    const opTurnsRef = currentProjectId ? getSessionRef(opKey, 'turnsRef', []) : turnsRef;
    const opPersistedTurnCountRef = currentProjectId ? getSessionRef(opKey, 'persistedTurnCountRef', 0) : persistedTurnCountRef;
    const opAssistantTurnMappingsRef = currentProjectId ? getSessionRef(opKey, 'assistantTurnMappingsRef', {}) : assistantTurnMappingsRef;
    const opSet = (field, updater) => setSessionValue(opKey, field, updater);
    const opUpdateTurns = (updater) => {
      const next = typeof updater === 'function' ? updater(opTurnsRef.current) : updater;
      opTurnsRef.current = next;
      opSet('turns', next);
      return next;
    };
    const opCommitTurns = (nextTurns) => { opTurnsRef.current = nextTurns; opSet('turns', nextTurns); };
    const opUpsertAssistantTurn = (assistantId, updater) => {
      const prevTurns = opTurnsRef.current;
      const index = prevTurns.findIndex(t => t.id === assistantId);
      let nextTurns;
      if (index === -1) {
        const created = typeof updater === 'function' ? updater(null) : updater;
        nextTurns = created ? [...prevTurns, created] : prevTurns;
      } else {
        const current = prevTurns[index];
        const updated = typeof updater === 'function' ? updater(current) : updater;
        if (!updated) { nextTurns = prevTurns; }
        else { nextTurns = [...prevTurns]; nextTurns[index] = updated; }
      }
      opTurnsRef.current = nextTurns;
      opSet('turns', nextTurns);
      return nextTurns;
    };
    const streamCtrl = new AbortController();
    streamAbortControllersRef.current.add(streamCtrl);

    try {

      // ── Step B: Optimistic UI update ──
      clientUserTurnId = generateId();
      clientAssistantTurnId = generateId();

      const userTurnNumber = opPersistedTurnCountRef.current + 1;

      // Create file metadata immediately from File objects
      // This gives you the display info (name, type, size) without waiting for upload
      let dbUserTurnId = null;
      if (currentProjectId) {
        try {
          const userRes = await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              turnNumber: userTurnNumber,
              role: 'user',
              content: text || '',
            }),
          });

          // ── H1: Handle duplicate turn number (409) explicitly ──
          if (userRes.status === 409) {
            console.warn('Turn number conflict on user turn — consuming number:', userTurnNumber);
            opPersistedTurnCountRef.current = userTurnNumber;
          } else {
            dbUserTurnId = (await userRes.json()).turnId;
            opPersistedTurnCountRef.current = userTurnNumber;
          }
        } catch (err) {
          console.error('Failed to create turn:', err);
          // non-fatal — continue without DB
        }
      }

      // ── Step B-UI: Optimistic UI update ──
      const immediateFileData = (files || []).map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        presigned_url: null,
        uploading: true,
      }));

      const newUserTurn = {
        id: clientUserTurnId,
        role: 'user',
        content: text || '',
        files: immediateFileData,
        turnNumber: userTurnNumber,
        tool_calls: [],
        toolCallId: null,
        kwargs: {},
      };

      opUpdateTurns(prev => [...prev, newUserTurn]);
      opSet('typingTurnId', clientAssistantTurnId);

      // Reset scroll tracking
      userScrolledAwayRef.current = false;
      userScrolledAwayRightRef.current = false;
      isNearBottomRef.current = true;
      setTimeout(() => {
        if (isActiveProject(currentProjectId || DRAFT_KEY)) scrollMessagesToBottom();
      }, 0);

      // ── Step B2: Upload files to S3 in background ──
      let uploadedMeta = []; // [{ name, type, url }, ...]
      if (files?.length > 0 && dbUserTurnId && currentProjectId) {
        const results = await Promise.all(
          files.map(async (file, i) => {
            try {
              const uploaded = await handleFileUpload(file, dbUserTurnId, currentProjectId);
              if (uploaded) {
                const normalizedUploaded = {
                  id: uploaded.id,
                  name: uploaded.filename,
                  type: uploaded.mimeType,
                  size: uploaded.size,
                  presigned_url: uploaded.presigned_url,
                  storagePath: uploaded.storagePath || uploaded.s3Key,
                  uploading: false,
                  uploadError: false,
                };
                opUpdateTurns(prev => prev.map(t => {
                  if (t.id !== clientUserTurnId) return t;
                  const updated = [...t.files];
                  updated[i] = normalizedUploaded;
                  return { ...t, files: updated };
                }));
                return { name: uploaded.filename, type: uploaded.mimeType, presigned_url: uploaded.presigned_url };
              }
            } catch {
              opUpdateTurns(prev => prev.map(t => {
                if (t.id !== clientUserTurnId) return t;
                const updated = [...t.files];
                updated[i] = { ...updated[i], uploading: false, uploadError: true };
                return { ...t, files: updated };
              }));
            }
            return null;
          })
        );

        uploadedMeta = results.filter(Boolean);

        if (uploadedMeta.length > 0) {
          opSet('allFiles', prev => [...prev, ...uploadedMeta]);
          // Per-file upload results are already patched into the turn's files array
          // by the individual opUpdateTurns calls above — no overwrite needed here.
        }
      }

      //  ── Step C: Agent chat ──
      const allFileMsgs = buildFileMessages([...allFiles, ...uploadedMeta]); // all turns' files
      // ^ allFiles has prior turns' metadata; uploadedMeta has this turn's new files

      const updatedValidMessages = text?.trim() ? [...validUserMessages, text] : validUserMessages;
      opSet('validUserMessages', updatedValidMessages);

      // ✅ Fire-and-forget — don't await, don't block streaming
      // Propagate the real persisted user Turn ID so the title-generation
      // invocation is attributable to that Turn. Falls back to project-level
      // accounting only when no real user Turn exists (legitimate edge case).
      if (isFirstMessage && currentProjectId) {
        fetchTopicName(text, currentProjectId, dbUserTurnId || null);
      }

      // ── Step D: Agent/chat + optimizations in parallel ──

      const agentPromise = async () => {
        // Track the active UI bubble — starts as the one created in Step B
        let activeClientAssistantId = clientAssistantTurnId;
        const capturedPid = currentProjectId;
        try {
          let streamed = '';
          let dbAssistantTurnId = null;
          let assistantTurnIsPersisted = false; // dbAssistantTurnId is a real DB Turn ID
          const attachedInvocationIds = new Set(); // tracks invocations attached across iterations
          let shouldStopAgent = false;

          while (true) {
            if (stopRequestedRef.current) {
              break;
            }

            // ── 1. Stream delta tokens into the current active bubble ──────────────
            const { finalData: agentResponse, invocationId: chatInvocationId } = await callAgent(opTurnsRef.current, (chunk) => {
              streamed += chunk;
              opUpsertAssistantTurn(activeClientAssistantId, (existing) => {
                if (existing) {
                  return { ...existing, content: streamed };
                }

                return {
                  id: activeClientAssistantId,
                  role: 'assistant',
                  content: streamed,
                  files: [],
                  turnNumber: opPersistedTurnCountRef.current + 1,
                  tool_calls: [],
                  toolCallId: null,
                  kwargs: {},
                };
              });
            }, streamCtrl.signal, buildAcctCtx(capturedPid, dbAssistantTurnId));

            // ── 2. Persist the assistant turn to DB (first iteration = create, rest = update) ──
            if (currentProjectId) {
              if (!dbAssistantTurnId) {
                // First iteration: CREATE the assistant turn row
                const assistantTurnNumber = opPersistedTurnCountRef.current + 1;
                const assistantRes = await fetchWithAuth(
                  `${API}/api/projects/${currentProjectId}/turns`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      turnNumber: assistantTurnNumber,
                      role: 'assistant',
                      content: streamed,
                    }),
                  }
                );
                // ── H1: Handle duplicate turn number (409) — consume number, use fallback id ──
                if (assistantRes.status === 409) {
                  console.warn('Turn number conflict on assistant turn — consuming number:', assistantTurnNumber);
                  dbAssistantTurnId = generateId(); // fallback id so PATCH calls don't crash
                  assistantTurnIsPersisted = false; // NOT a real Turn — never attach it
                  opPersistedTurnCountRef.current = assistantTurnNumber;
                } else {
                  const { turnId } = await assistantRes.json();
                  dbAssistantTurnId = turnId;
                  assistantTurnIsPersisted = true; // real persisted Turn ID
                  opAssistantTurnMappingsRef.current[activeClientAssistantId] = turnId;
                  opPersistedTurnCountRef.current = assistantTurnNumber;
                }
              }

              // ── 2b. Attach this iteration's chat invocation to the real assistant Turn ──
              // Each /agent/chat invocation is started with turnId = null before the provider call;
              // once that iteration's real assistant Turn exists, backfill the Turn ID onto it.
              // Best-effort — never blocks the conversation, never uses a synthetic ID.
              if (chatInvocationId && assistantTurnIsPersisted && dbAssistantTurnId && !attachedInvocationIds.has(chatInvocationId)) {
                try {
                  await attachInvocationToTurn(chatInvocationId, dbAssistantTurnId);
                  attachedInvocationIds.add(chatInvocationId);
                } catch (err) {
                  console.error('Failed to attach chat invocation to assistant turn:', err);
                }
              }

              // Always PATCH: save content + metadata (strip 'content' key) + toolCalls
              const { content: _content, ...metadataToSave } = agentResponse || {};
              await patchTurn(currentProjectId, dbAssistantTurnId, {
                content: streamed,
                metadata: metadataToSave,
                toolCalls: agentResponse?.tool_calls ?? [],
                status: 'completed',
              });
            }

            // ── If cancelled, break before processing tool calls or further loops ──
            if (stopRequestedRef.current || agentResponse?.cancelled || agentResponse?.response_metadata?.stop_reason === 'cancelled') {
              opUpdateTurns(prev =>
                prev.map(t =>
                  t.id === activeClientAssistantId ? { ...t, content: streamed } : t
                )
              );
              break;
            }

            // ── 3. No tool_calls → loop ends, we're done ───────────────────────────
            const toolCalls = agentResponse.tool_calls;
            if (!toolCalls || toolCalls.length === 0) {
              opUpdateTurns(prev =>
                prev.map(t =>
                  t.id === activeClientAssistantId ? { ...t, content: streamed } : t
                )
              );
              break;
            }

            // If stop was requested while callAgent was finishing, do not run tool calls
            if (stopRequestedRef.current) {
              break;
            }

            opUpsertAssistantTurn(activeClientAssistantId, (existing) => ({
              ...(existing || {
                id: activeClientAssistantId,
                role: 'assistant',
                content: streamed,
                files: [],
                turnNumber: opPersistedTurnCountRef.current + 1,
                tool_calls: [],
                toolCallId: null,
                kwargs: {},
              }),
              content: streamed,
              tool_calls: toolCalls.map(tc => ({
                id: tc.id,
                name: tc.name,
                args: tc.args,
              })),
            }));

            for (const tc of toolCalls) {
              if (stopRequestedRef.current) {
                shouldStopAgent = true;
                break;
              }
              let toolResult;
              let toolContent = '';
              let historyRole = 'tool';
              let dbRole = 'tool';

              // ─────────────────────────────────────────────────────
              // get_analysis: stream to right pane, persist content
              // then patch kwargs with extracted suggestions
              // ─────────────────────────────────────────────────────
              if (tc.name === 'get_analysis') {
                let streamedAnalysis = '';

                // ── Step 1: CREATE analysis turn with empty content ──
                let dbAnalysisTurnId = null;
                let analysisTurnData = null;
                if (currentProjectId) {
                  const analysisTurnNumber = opPersistedTurnCountRef.current + 1;
                  const analysisTurnRes = await fetchWithAuth(
                    `${API}/api/projects/${currentProjectId}/turns`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        turnNumber: analysisTurnNumber,
                        role: 'analysis',
                        content: '',
                        toolCallId: tc.id,
                      }),
                    }
                  );
                  analysisTurnData = await analysisTurnRes.json();
                  dbAnalysisTurnId = analysisTurnData.turnId;
                  opPersistedTurnCountRef.current = analysisTurnNumber;
                }
                opUpdateTurns(prev => [
                  ...prev,
                  {
                    id: dbAnalysisTurnId,
                    role: 'analysis',
                    content: '',
                    tool_call_id: tc.id,
                    tool_calls: [],
                    files: [],
                    kwargs: {},
                    status: 'streaming',
                  },
                ]);
                opSet('activeSuggestionTurnId', dbAnalysisTurnId);
                opSet('typingSuggestionId', dbAnalysisTurnId);
                opSet('suggestions', prev => ({ ...prev, [dbAnalysisTurnId]: '' }));
                userScrolledAwayRightRef.current = false;

                try {
                  toolResult = await streamAnalysisTool(tc.args, opTurnsRef.current, (chunk) => {
                    streamedAnalysis += chunk;
                    opSet('suggestions', prev => ({
                      ...prev,
                      [dbAnalysisTurnId]: streamedAnalysis,
                    }));
                    setTimeout(() => {
                      if (!userScrolledAwayRightRef.current && isActiveProject(capturedPid)) scrollRightToBottom();
                    }, 0);
                  }, streamCtrl.signal, buildAcctCtx(capturedPid, dbAnalysisTurnId));
                } finally {
                  opSet('typingSuggestionId', null);
                  userScrolledAwayRightRef.current = false;
                }

                toolContent = JSON.stringify(toolResult);
                historyRole = 'analysis';
                dbRole = 'analysis';
                if (currentProjectId) {
                  // ── Step 2: PATCH content + status ──
                  await patchTurn(currentProjectId, dbAnalysisTurnId, {
                    content: streamedAnalysis,
                    metadata: toolResult,
                    status: 'completed',
                  });
                }

                // ── If analysis stream was cancelled, halt tool loop and skip extraction ──
                if (stopRequestedRef.current || toolResult?.cancelled || toolResult?.response_metadata?.stop_reason === 'cancelled') {
                  opUpdateTurns(prev =>
                    prev.map(turn =>
                      turn.id === dbAnalysisTurnId
                        ? {
                          ...turn,
                          content: streamedAnalysis,
                          status: 'completed',
                        }
                        : turn
                    )
                  );
                  shouldStopAgent = true;
                  break;
                }

                if (stopRequestedRef.current) {
                  shouldStopAgent = true;
                  break;
                }

                // ── Step 3: extract optimizations (non-stream JSON call) ──
                opSet('isLoadingSuggestions', true);
                opSet('suggestionExtractionStatus', prev => ({
                  ...prev,
                  [dbAnalysisTurnId]: 'loading',
                }));
                let extracted = {};
                try {
                  const extractRes = await extractOptimizations(streamedAnalysis, buildAcctCtx(capturedPid, dbAnalysisTurnId));
                  const raw =
                    extractRes?.data.content ??
                    extractRes ??
                    {};
                  extracted =
                    raw && typeof raw === 'object' && !Array.isArray(raw)
                      ? Object.fromEntries(
                        Object.entries(raw).filter(
                          ([k, v]) => typeof k === 'string' && k.trim() && typeof v === 'string'
                        )
                      )
                      : {};
                } catch (err) {
                  console.error('extractOptimizations failed (non-fatal):', err);
                } finally {
                  opSet('isLoadingSuggestions', false);
                }

                // ── Step 4: update in-memory UI state ──
                opSet('extractedSuggestions', prev => ({
                  ...prev,
                  [dbAnalysisTurnId]: extracted,
                }));
                opSet('suggestionExtractionStatus', prev => ({
                  ...prev,
                  [dbAnalysisTurnId]: Object.keys(extracted).length > 0 ? 'done' : 'idle',
                }));


                // ── Step 5: PATCH kwargs onto the analysis turn ──
                if (currentProjectId && dbAnalysisTurnId && Object.keys(extracted).length > 0) {
                  await patchTurn(currentProjectId, dbAnalysisTurnId, {
                    kwargs: {
                      extract_optimization: extracted,
                    },
                  }).catch(err => console.error('kwargs patch failed (non-fatal):', err));
                }

                opUpdateTurns(prev =>
                  prev.map(turn =>
                    turn.id === dbAnalysisTurnId
                      ? {
                        ...turn,
                        content: streamedAnalysis,
                        kwargs: {
                          extract_optimization: extracted,
                        },
                        status: 'completed',
                      }
                      : turn
                  )
                );
                shouldStopAgent = true;
                break;
              }

              // ─────────────────────────────────────────────────────
              // All other tools: normal JSON execute-tool
              // ─────────────────────────────────────────────────────

              const toolRes = await fetchWithAuth(`${API}/api/analysis/execute-tool`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: tc.name, args: tc.args, ...buildAcctCtx(capturedPid, null) }),
              });

              const toolJson = await toolRes.json();
              toolResult = toolJson;
              toolContent = JSON.stringify(toolJson.data);
              let toolTurnResData = null;
              // Generic tool-turn persistence
              if (currentProjectId) {
                const toolTurnNumber = opPersistedTurnCountRef.current + 1;
                const toolTurnRes = await fetchWithAuth(
                  `${API}/api/projects/${currentProjectId}/turns`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      turnNumber: toolTurnNumber,
                      role: dbRole,
                      content: toolContent,
                      toolCallId: tc.id,
                    }),
                  }
                );
                // ── H1: Handle duplicate turn number (409) — consume number, use fallback id ──
                if (toolTurnRes.status === 409) {
                  console.warn('Turn number conflict on tool turn — consuming number:', toolTurnNumber);
                  toolTurnResData = { id: generateId() };
                  opPersistedTurnCountRef.current = toolTurnNumber;
                } else {
                  toolTurnResData = await toolTurnRes.json();
                  opPersistedTurnCountRef.current = toolTurnNumber;
                }
              }

              opUpdateTurns(prev => [
                ...prev,
                {
                  id: toolTurnResData?.id,
                  role: historyRole,
                  content: toolContent,
                  tool_call_id: tc.id,
                  tool_calls: [],
                  files: [],
                  kwargs: {},
                },
              ]);
            }

            if (shouldStopAgent || stopRequestedRef.current) {
              break;
            }

            // ── 5. Spawn a NEW assistant bubble in the UI for the next iteration ───
            const nextClientAssistantId = generateId();

            // Move the typing cursor to the new bubble
            opSet('typingTurnId', nextClientAssistantId);
            setTimeout(() => {
              if (!userScrolledAwayRef.current && isActiveProject(capturedPid)) scrollMessagesToBottom();
            }, 0);

            streamed = '';
            dbAssistantTurnId = null;
            assistantTurnIsPersisted = false;
            activeClientAssistantId = nextClientAssistantId;
          }

        } catch (err) {
          if (err.message === 'STREAM_ABORTED') return; // intentional abort — silent cleanup
          console.error('Agent error', err);
          if (currentProjectId && dbUserTurnId) {
            await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns/${dbUserTurnId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'rejected' }),
            }).catch(() => { });

            // Mirror the DB rejection onto the local user turn so getApiTurns()
            // excludes it from future API payloads in this same session.
            opUpdateTurns(prev =>
              prev.map(t => t.id === clientUserTurnId ? { ...t, status: 'rejected' } : t)
            );
          }
          // Phase 6 (FINAL): a credit 402 shows the product-friendly
          // credit-unavailable message, surfaces the persistent exhausted
          // chat-area alert, and disables Send (server refresh). No modal,
          // no toast, no technical details.
          const isCredit402 = err && err.status === 402 && err.creditCode;
          if (isCredit402) {
            credit.handleCredit402(err.creditCode);
          }
          const errorMessage = isCredit402 ? CREDIT_UNAVAILABLE_MESSAGE : 'An unexpected error occurred!';
          let streamed = '';
          await streamText(errorMessage, (ch) => { streamed += ch; }, 15, capturedPid);
          opUpsertAssistantTurn(activeClientAssistantId, (existing) => ({
            ...(existing || {
              id: activeClientAssistantId,
              role: 'assistant',
              content: '',
              files: [],
              turnNumber: opPersistedTurnCountRef.current + 1,
              tool_calls: [],
              toolCallId: null,
              kwargs: {},
            }),
            content: streamed,
          }));
          throw new Error('AGENT_REJECTED');

        } finally {
          opSet('typingTurnId', null);
          userScrolledAwayRef.current = false;
          streamAbortControllersRef.current.delete(streamCtrl);
        }
      };
      await agentPromise();

    } catch (err) {
      if (err?.message !== 'AGENT_REJECTED') {
        opSet('isLoadingSuggestions', false);
        console.error('handleSend error:', err);

        const errorMessage = 'Sorry, I encountered an error processing your request. Please try again.';
        let streamed = '';
        await streamText(errorMessage, (ch) => {
          streamed += ch;
          opUpsertAssistantTurn(clientAssistantTurnId, (existing) => ({
            ...(existing || {
              id: clientAssistantTurnId,
              role: 'assistant',
              content: '',
              files: [],
              turnNumber: opPersistedTurnCountRef.current + 1,
              tool_calls: [],
              toolCallId: null,
              kwargs: {},
            }),
            content: streamed,
          }));
        });
        opSet('typingTurnId', null);
        userScrolledAwayRef.current = false;
      }
    } finally {
      opSet('isSending', false);
      stopRequestedRef.current = false;
      isStoppingRef.current = false;
      cancelledRequestIdsRef.current.clear();
      setIsStopping(false);
      streamAbortControllersRef.current.delete(streamCtrl);
    }
  };

  // ─── Report generation ─────────────────────────────────────────
  const handleGenerateReport = async (turnId, overrideSelections = null) => {
    // ── B2: Prevent concurrent send+report for the same chat ──
    if (isSending || isLoadingReport || Boolean(typingReportId)) return;

    stopRequestedRef.current = false;
    isStoppingRef.current = false;
    cancelledRequestIdsRef.current.clear();
    setIsStopping(false);

    const checkedSuggestions = overrideSelections !== null
      ? overrideSelections
      : (selectedSuggestions[turnId] || []);

    // ── Operation-scoped — immutable project identity ────────────
    const opKey = projectId;
    if (!opKey) return;
    const opTurnsRef = getSessionRef(opKey, 'turnsRef', []);
    const opPersistedTurnCountRef = getSessionRef(opKey, 'persistedTurnCountRef', 0);
    const opSet = (field, updater) => setSessionValue(opKey, field, updater);
    const opUpdateTurns = (updater) => {
      const next = typeof updater === 'function' ? updater(opTurnsRef.current) : updater;
      opTurnsRef.current = next;
      opSet('turns', next);
      return next;
    };

    // ── Per-operation AbortController (after early-return guards) ──
    const analysisTurn = opTurnsRef.current.find(turn => turn.id === turnId);
    if (!analysisTurn) return;
    const streamCtrl = new AbortController();
    streamAbortControllersRef.current.add(streamCtrl);

    // ── Operation-scoped turn creation helper ────────────────────
    const opCreateAndAppendTurn = async (payload) => {
      const res = await fetchWithAuth(`${API}/api/projects/${opKey}/turns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      // ── H1: Handle duplicate turn number (409) — consume number, continue ──
      if (res.status === 409) {
        console.warn('Turn number conflict on report turn — consuming number:', payload.turnNumber);
        opPersistedTurnCountRef.current = payload.turnNumber;
        const fallbackId = generateId();
        const localTurn = { ...createLocalTurnFromPayload(fallbackId, payload), id: fallbackId };
        opUpdateTurns(prev => [...prev, localTurn]);
        return { dbTurnId: fallbackId, localTurn };
      }
      if (!res.ok) throw new Error(data.error || `Failed to create ${payload.role} turn`);
      const dbTurnId = data.turnId || data.id;
      opPersistedTurnCountRef.current = payload.turnNumber;
      const localTurn = { ...createLocalTurnFromPayload(dbTurnId, payload), id: dbTurnId };
      opUpdateTurns(prev => [...prev, localTurn]);
      return { dbTurnId, localTurn };
    };

    const acceptedOptimizations = buildAcceptedOptimizations(turnId, checkedSuggestions);
    const currentReportCount = getReportCountFromKwargs(analysisTurn.kwargs);
    const newVersion = currentReportCount + 1;
    const toolCallId = generateToolCallId();
    let assistantReportTurnId = null;
    let reportTurnId = null;

    try {
      opSet('isLoadingReport', true);
      opSet('reportLoadingPhase', 'costs');
      userScrolledAwayRightRef.current = false;
      const assistantPayload = {
        turnNumber: opPersistedTurnCountRef.current + 1,
        role: 'assistant',
        content: '',
        status: 'pending',
        toolCalls: [{ name: 'get_material_cost_list', args: {}, id: toolCallId, type: 'tool_call' }],
      };

      const assistantTurnResult = await opCreateAndAppendTurn(assistantPayload);
      assistantReportTurnId = assistantTurnResult.dbTurnId;

      const turnsForToolCall = opTurnsRef.current;

      const toolResult = await getMaterialCostList(turnsForToolCall, checkedSuggestions, buildAcctCtx(opKey, turnId));
      const toolContent =
        typeof toolResult?.data === 'string'
          ? toolResult.data
          : JSON.stringify(toolResult?.data ?? toolResult ?? {});

      const toolPayload = {
        turnNumber: opPersistedTurnCountRef.current + 1,
        role: 'tool',
        content: toolContent,
        status: 'completed',
        toolCallId,
        kwargs: {},
      };

      await opCreateAndAppendTurn(toolPayload);

      if (opKey && assistantReportTurnId) {
        await patchTurn(opKey, assistantReportTurnId, { status: 'completed' });
      }

      opUpdateTurns(prev => prev.map(turn => (
        turn.id === assistantReportTurnId ? { ...turn, status: 'completed' } : turn
      )));

      const reportPayload = {
        turnNumber: opPersistedTurnCountRef.current + 1,
        role: 'report',
        content: '',
        status: 'pending',
        kwargs: { accepted_optimizations: checkedSuggestions, analysis_turn_id: turnId, version: newVersion },
      };

      const reportTurnResult = await opCreateAndAppendTurn(reportPayload);
      reportTurnId = reportTurnResult.dbTurnId;

      opSet('reports', prev => {
        const currentEntries = prev[turnId] || [];
        return {
          ...prev,
          [turnId]: [...currentEntries, {
            turnId: reportTurnId, version: newVersion, content: '', isComplete: false,
            status: 'pending', selectedSuggestions: Object.keys(acceptedOptimizations),
            acceptedOptimizations, analysisTurnId: turnId,
          }],
        };
      });

      opSet('activeSuggestionTurnId', turnId);
      opSet('activeReportId', { turnId, version: newVersion, reportTurnId });
      opSet('typingReportId', { turnId, version: newVersion, reportTurnId });

      const turnsForReport = opTurnsRef.current;

      opSet('reportLoadingPhase', 'streaming');
      let streamedReport = '';
      if (stopRequestedRef.current) {
        if (opKey && reportTurnId) {
          await patchTurn(opKey, reportTurnId, { content: '', status: 'completed' }).catch(() => {});
        }
        opUpdateTurns(prev => prev.map(turn => (
          turn.id === reportTurnId ? { ...turn, content: '', status: 'completed' } : turn
        )));
        opSet('reports', prev => {
          const entries = (prev[turnId] || []).map(e =>
            e.turnId === reportTurnId ? { ...e, content: '', isComplete: false, status: 'completed' } : e
          );
          return { ...prev, [turnId]: entries };
        });
        return;
      }
      let firstChunk = false;

      const finalEventData = await streamReport(turnsForReport, checkedSuggestions, (chunk) => {
        if (!firstChunk) {
          opSet('isLoadingReport', false);
          firstChunk = true;
        }
        streamedReport += chunk;
        opUpdateTurns(prev => prev.map(turn => (
          turn.id === reportTurnId
            ? { ...turn, content: streamedReport, status: 'pending' }
            : turn
        )));
        opSet('reports', prev => {
          const entries = (prev[turnId] || []).map(e =>
            e.turnId === reportTurnId ? { ...e, content: streamedReport } : e
          );
          return { ...prev, [turnId]: entries };
        });
        setTimeout(() => {
          if (!userScrolledAwayRightRef.current && isActiveProject(opKey)) scrollRightToBottom();
        }, 0);
      }, streamCtrl.signal, buildAcctCtx(opKey, reportTurnId));

      // ── Save report to DB after streaming completes ──

      if (opKey && reportTurnId) {
        await patchTurn(opKey, reportTurnId, {
          content: streamedReport,
          metadata: finalEventData,
          status: 'completed',
        });

        await patchTurn(opKey, turnId, {
          kwargs: {
            ...(analysisTurn.kwargs || {}),
            extract_optimization: getExtractOptimizationFromKwargs(analysisTurn.kwargs),
            report_count: newVersion,
            [`report_v${newVersion}`]: reportTurnId,
          },
        });
      }

      // Cache PDF (only if report was not cancelled)
      const reportWasCancelled = stopRequestedRef.current || finalEventData?.cancelled || finalEventData?.response_metadata?.stop_reason === 'cancelled';
      if (!reportWasCancelled && streamedReport) {
        try {
          const pdfRes = await fetchWithAuth(`${API}/pdf/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ markdown: streamedReport, title: 'Alpherra Report', version: newVersion }),
          });
          if (pdfRes.ok) {
            const pdfBlob = await pdfRes.blob();
            await savePDFToCache(turnId, newVersion, pdfBlob);
          }
        } catch (err) {
          console.warn('PDF cache/upload failed:', err);
        }
      }

      opUpdateTurns(prev => prev.map(turn => {
        if (turn.id === reportTurnId) {
          return { ...turn, content: streamedReport, metadata: finalEventData, status: 'completed' };
        }
        if (turn.id === turnId) {
          return { ...turn, kwargs: { ...(turn.kwargs || {}),
            extract_optimization: getExtractOptimizationFromKwargs(turn.kwargs),
            report_count: newVersion, [`report_v${newVersion}`]: reportTurnId } };
        }
        return turn;
      }));

      opSet('reports', prev => {
        const entries = (prev[turnId] || []).map(e =>
          e.turnId === reportTurnId ? { ...e, content: streamedReport, isComplete: !reportWasCancelled, status: 'completed' } : e
        );
        return { ...prev, [turnId]: entries };
      });

    } catch (err) {
      if (err.message === 'STREAM_ABORTED') { streamAbortControllersRef.current.delete(streamCtrl); return; }
      console.error('Report generation error:', err);

      // Phase 6 (FINAL): a credit 402 shows the product-friendly
      // credit-unavailable message, surfaces the persistent exhausted
      // chat-area alert, and disables Send (server refresh). No modal,
      // no toast, no technical details.
      const isCredit402 = err && err.status === 402 && err.creditCode;
      if (isCredit402) {
        credit.handleCredit402(err.creditCode);
      }
      const reportErrorText = isCredit402 ? CREDIT_UNAVAILABLE_MESSAGE : '[Error generating report]';

      if (opKey && assistantReportTurnId && !reportTurnId) {
        await patchTurn(opKey, assistantReportTurnId, { status: 'rejected' }).catch(() => { });
      }

      if (opKey && reportTurnId) {
        await patchTurn(opKey, reportTurnId, { status: 'rejected' }).catch(() => { });
        await patchTurn(opKey, turnId, {
          kwargs: { ...(analysisTurn.kwargs || {}),
            extract_optimization: getExtractOptimizationFromKwargs(analysisTurn.kwargs),
            report_count: newVersion, [`report_v${newVersion}`]: reportTurnId },
        }).catch(() => { });
      }

      opUpdateTurns(prev => prev.map(turn => (
        turn.id === reportTurnId
          ? { ...turn, content: turn.content ? `${turn.content}\n\n${reportErrorText}` : reportErrorText, status: 'rejected' }
          : turn.id === assistantReportTurnId && !reportTurnId
            ? { ...turn, status: 'rejected' }
            : turn.id === turnId
              ? { ...turn, kwargs: { ...(turn.kwargs || {}),
                  extract_optimization: getExtractOptimizationFromKwargs(turn.kwargs),
                  report_count: newVersion, [`report_v${newVersion}`]: reportTurnId } }
              : turn
      )));

      opSet('reports', prev => {
        const entries = (prev[turnId] || []).map(e =>
          e.turnId === (reportTurnId || `failed_report_${turnId}_${newVersion}`)
            ? { ...e, content: reportErrorText, isComplete: true, status: 'rejected' }
            : e
        );
        if (!entries.find(e => e.turnId === (reportTurnId || `failed_report_${turnId}_${newVersion}`))) {
          entries.push({ turnId: reportTurnId || `failed_report_${turnId}_${newVersion}`, version: newVersion,
            content: reportErrorText, isComplete: true, status: 'rejected',
            selectedSuggestions: Object.keys(acceptedOptimizations), acceptedOptimizations, analysisTurnId: turnId });
        }
        return { ...prev, [turnId]: entries };
      });
    } finally {
      stopRequestedRef.current = false;
      isStoppingRef.current = false;
      cancelledRequestIdsRef.current.clear();
      setIsStopping(false);
      opSet('typingReportId', null);
      userScrolledAwayRightRef.current = false;
      opSet('isLoadingReport', false);
      opSet('reportLoadingPhase', 'init');
      streamAbortControllersRef.current.delete(streamCtrl);
    }
  };

  // ─── Feedback handlers ─────────────────────────────────────────
  // ── Unified feedback (handles assistant, analysis, and report turns) ──
  const saveFeedback = async (turnId, feedbackType, comment = '') => {
    if (!turnId || !projectId) return;

    try {
      await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${turnId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: feedbackType, comment }),
      });
    } catch (err) {
      console.error('Feedback save error:', err);
    }
  };

  // ─── Toggle handlers ───────────────────────────────────────────
  const toggleSuggestion = (id) => {
    if (activeSuggestionTurnId === id && !activeReportId) {
      setActiveSuggestionTurnId(null);
    } else {
      setActiveSuggestionTurnId(id);
      setActiveReportId(null);
    }
  };

  const toggleReport = (turnId, version) => {
    if (activeReportId?.turnId === turnId && activeReportId?.version === version) {
      setActiveReportId(null);
      setActiveSuggestionTurnId(null);
    } else {
      const report = (reports[turnId] || []).find(entry => entry.version === version) || null;
      setActiveReportId({
        turnId,
        version,
        reportTurnId: report?.turnId || null,
      });
      setActiveSuggestionTurnId(turnId);
    }
  };

  const confirmSuggestionSelection = (turnId, checkedKeys) => {
    setSelectedSuggestions(prev => ({ ...prev, [turnId]: checkedKeys }));
  };

  const stopGeneration = useCallback(async () => {
    // 1. Synchronously guard against rapid double-clicks or repeated clicks
    if (isStoppingRef.current || stopRequestedRef.current) {
      return;
    }
    isStoppingRef.current = true;
    stopRequestedRef.current = true;
    setIsStopping(true);

    const currentRequestId = activeRequestIdRef.current;
    if (currentRequestId) {
      await sendCancelRequest(currentRequestId);
    }
    // If currentRequestId is not yet populated (connecting phase),
    // stopRequestedRef.current is now true, so as soon as response headers
    // arrive with x-request-id in callAgent/streamAnalysisTool/streamReport,
    // sendCancelRequest(requestId) will be called immediately.
  }, [sendCancelRequest]);

  // ─── Computed values ───────────────────────────────────────────
  const isRightPaneOpen = !!activeSuggestionTurnId;
  const activeTurn = activeSuggestionTurnId
    ? turns.find(t => t.id === activeSuggestionTurnId) || null
    : null;
  // const isRightPaneOpen = !!activeTurn;

  const getRightPaneContent = () => {
    if (!activeSuggestionTurnId) return null;
    if (activeReportId && activeReportId.turnId === activeSuggestionTurnId) {
      const reportEntry = (reports[activeReportId.turnId] || []).find(
        report => report.version === activeReportId.version
      );
      const reportTurn = activeReportId.reportTurnId
        ? turns.find(turn => turn.id === activeReportId.reportTurnId && turn.role === 'report')
        : null;

      return {
        type: 'report',
        turnId: reportEntry?.turnId || activeReportId.reportTurnId || null,
        analysisTurnId: activeReportId.turnId,
        content: reportTurn?.content ?? reportEntry?.content ?? '',
        isComplete: reportEntry?.isComplete || reportTurn?.status === 'completed' || false,
        status: reportEntry?.status || reportTurn?.status || 'pending',
        version: activeReportId.version,
        selectedSuggestions: reportEntry?.selectedSuggestions || [],
        acceptedOptimizations: reportEntry?.acceptedOptimizations || {},
      };
    }
    return {
      type: 'suggestions',
      content: suggestions[activeSuggestionTurnId] || '',
      extracted: extractedSuggestions[activeSuggestionTurnId] || {},
      selected: selectedSuggestions[activeSuggestionTurnId] || [],
    };
  };

  // ─── Exports ───────────────────────────────────────────────────
  return {
    chatStarted,
    isSending,
    turns,
    activeSuggestionTurnId,
    suggestions,
    typingTurnId,
    typingSuggestionId,
    activeTurn,
    isRightPaneOpen,
    extractedSuggestions,
    selectedSuggestions,
    reports,
    activeReportId,
    typingReportId,
    isLoadingSuggestions,
    isLoadingReport,
    isChatHydrating,
    reportLoadingPhase,
    setReportLoadingPhase,
    messagesContainerRef,
    rightPaneRef,

    // Project
    projectId,
    projectName,

    // Pagination
    hasMoreTurns,
    isLoadingOlderTurns,

    //feedback
    feedbackState, setFeedbackState,

    // Handlers
    handleSend,
    toggleSuggestion,
    toggleReport,
    onScrollMessages,
    handleRightPaneScroll,
    closeSuggestionPane: () => { setActiveSuggestionTurnId(null); setActiveReportId(null); },
    confirmSuggestionSelection,
    handleGenerateReport,
    getRightPaneContent,
    saveFeedback,
    suggestionExtractionStatus,
    onEnsureExtractedSuggestions,

    resetChat,
    loadProject,
    loadOlderTurns,
    renameProject,
    stopGeneration,
    isStopping,
  };
}
